import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Watch, Activity, Zap, Moon, HeartPulse, Plus, Smartphone, ShieldCheck,
  QrCode, X, Copy, Info, Trash2, Wind, Weight, Brain, Timer, Route, Flame,
  ChevronRight, CheckCircle2, AlertCircle, Lock, GripVertical,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";
import SortableGrid from "@/components/SortableGrid";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";

const PROVIDERS = [
  { id: "apple_health", name: "Apple Health", icon: Watch, desc: "iPhone & Apple Watch — native iOS app required", featured: true },
  { id: "garmin", name: "Garmin Connect", icon: Activity, desc: "Coming soon — pending Garmin Developer contract", locked: true },
  { id: "fitbit", name: "Fitbit", icon: HeartPulse, desc: "Fitbit devices" },
  { id: "whoop", name: "WHOOP", icon: Zap, desc: "Recovery & strain tracking" },
  { id: "oura", name: "Oura Ring", icon: Moon, desc: "Sleep & readiness" },
];

// Data categories the client can opt in / out of before uploads reach Functional LAB.
const HEALTH_CATEGORIES = [
  { key: "steps", label: "Steps", icon: Route, desc: "Daily step count" },
  { key: "distance", label: "Walking & running distance", icon: Activity, desc: "Distance in km" },
  { key: "exercise", label: "Exercise minutes", icon: Timer, desc: "Active exercise duration" },
  { key: "heartRate", label: "Heart-rate summaries", icon: HeartPulse, desc: "Resting & latest heart rate" },
  { key: "hrv", label: "Heart-rate variability", icon: Activity, desc: "HRV (SDNN, ms)" },
  { key: "sleep", label: "Sleep duration", icon: Moon, desc: "Sleep hours per night" },
  { key: "vo2", label: "VO₂ max", icon: Wind, desc: "Cardiorespiratory fitness" },
  { key: "spo2", label: "Blood oxygen (SpO₂)", icon: Wind, desc: "Oxygen saturation" },
  { key: "weight", label: "Body weight", icon: Weight, desc: "Body mass in kg" },
  { key: "mindful", label: "Mindful minutes", icon: Brain, desc: "Meditation & mindful sessions" },
  { key: "workouts", label: "Workouts", icon: Flame, desc: "Running, cycling, gym, swim etc." },
];

export default function PatientWearables() {
  const { t } = useTranslation();
  const [conns, setConns] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [manual, setManual] = useState({ date: new Date().toISOString().slice(0, 10), steps: "", resting_hr: "", sleep_minutes: "", active_minutes: "" });
  // Apple Health specific state
  const [consent, setConsent] = useState({ granted: false, categories: [] });
  const [snapshot, setSnapshot] = useState(null);
  const [devices, setDevices] = useState([]);
  const [showPair, setShowPair] = useState(false);
  const [pairing, setPairing] = useState(null); // {token, code, ingest_url, expires_at}
  // iter101.5 · rearrangeable "Other devices" widget grid
  const otherProviderIds = PROVIDERS.filter((p) => p.id !== "apple_health").map((p) => p.id);
  const { editing: editLayout } = useEditMode();
  const {
    visibleOrder: provOrder, setOrder: setProvOrder,
    toggleHidden: hideProv, resetToDefault: resetProv,
    setSize: sizeProv, sizes: provSizes,
    isDirty: provDirty,
  } = useDashboardLayout("patient_wearables_others", otherProviderIds);

  const refresh = async () => {
    const [c, m, cs, ls, dv] = await Promise.all([
      api.get("/wearables/connections"),
      api.get("/wearables/mine"),
      api.get("/v1/client-health/consent").catch(() => ({ data: { granted: false, categories: [] } })),
      api.get("/v1/client-health/latest").catch(() => ({ data: { snapshot: null } })),
      api.get("/wearables/devices").catch(() => ({ data: [] })),
    ]);
    setConns(c.data); setMetrics(m.data); setConsent(cs.data); setSnapshot(ls.data); setDevices(dv.data);
  };
  useEffect(() => { refresh(); }, []);

  const status = (id) => conns.find((c) => c.provider === id)?.status;

  const connect = async (id) => {
    try { await api.post("/wearables/connect", { provider: id }); toast.success("Device connected. Data will sync as it arrives."); refresh(); }
    catch { toast.error("Connection failed"); }
  };
  const disconnect = async (id) => {
    try { await api.post("/wearables/disconnect", { provider: id }); toast.success("Disconnected"); refresh(); }
    catch { toast.error("Failed"); }
  };

  const toggleCategory = (key) => {
    const next = consent.categories.includes(key) ? consent.categories.filter((c) => c !== key) : [...consent.categories, key];
    setConsent({ ...consent, categories: next });
  };
  const saveConsent = async (granted) => {
    try {
      const { data } = await api.post("/v1/client-health/consent", {
        granted,
        categories: granted ? (consent.categories.length ? consent.categories : HEALTH_CATEGORIES.map((c) => c.key)) : consent.categories,
      });
      setConsent(data);
      toast.success(granted ? "Consent granted. The iPhone app can now upload data." : "Consent revoked. Uploads will be rejected.");
    } catch { toast.error("Could not save consent"); }
  };
  const startPairing = async () => {
    try {
      const { data } = await api.post("/wearables/pair/start");
      setPairing(data);
      setShowPair(true);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start pairing"); }
  };
  const revokeDevice = async (id) => {
    if (!window.confirm("Revoke this paired device? Future uploads from it will be blocked.")) return;
    try { await api.post(`/wearables/devices/${id}/revoke`); toast.success("Device revoked"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const addManual = async () => {
    const payload = { provider: "manual", date: manual.date };
    ["steps", "resting_hr", "sleep_minutes", "active_minutes"].forEach((k) => {
      if (manual[k] !== "") payload[k] = parseInt(manual[k]);
    });
    if (Object.keys(payload).length <= 2) return toast.error("Fill at least one value");
    try {
      await api.post("/wearables/data", { metrics: [payload] });
      toast.success("Added");
      setManual({ ...manual, steps: "", resting_hr: "", sleep_minutes: "", active_minutes: "" });
      refresh();
    } catch { toast.error("Failed"); }
  };

  const chartData = useMemo(() => [...metrics].reverse().slice(-30).map((m) => ({
    date: new Date(m.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    steps: m.steps || 0, hr: m.resting_hr || 0, sleep: m.sleep_minutes ? (m.sleep_minutes / 60).toFixed(1) : 0,
  })), [metrics]);

  const s = snapshot?.snapshot;
  const appleConn = conns.find((c) => c.provider === "apple_health");

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.wearablesX.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.wearablesX.h1")}</h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--fl-muted)" }}>
          {t("pages.wearablesX.sub")}
        </p>
      </div>

      {/* Apple Health hero card — the recommended integration */}
      <div className="fl-card p-6 mb-8 relative overflow-hidden" data-testid="apple-health-card" style={{ borderColor: "var(--fl-orange)" }}>
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,69,0,0.20), transparent 70%)" }} />
        <div className="relative flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,69,0,0.12)" }}>
            <Watch className="text-[#f47a20]" size={26} />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="fl-heading text-2xl">Apple Health</div>
              {snapshot?.date && (
                <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "rgba(34,197,94,0.12)", color: "#22C55E" }}>
                  <CheckCircle2 size={12} /> Synced {new Date(snapshot.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </span>
              )}
              {consent.granted ? (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded" style={{ background: "rgba(34,197,94,0.12)", color: "#22C55E" }}>
                  <ShieldCheck size={10} className="inline mr-1" /> Consented
                </span>
              ) : (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded" style={{ background: "rgba(234,179,8,0.12)", color: "#EAB308" }}>
                  <AlertCircle size={10} className="inline mr-1" /> Consent required
                </span>
              )}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>
              iPhone & Apple Watch. Data flows: Apple Watch → Health app → <b>Functional LAB iOS app</b> → secure server → this dashboard. The web app <u>cannot</u> read HealthKit directly.
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button className="fl-btn text-sm" onClick={startPairing} data-testid="apple-pair-btn">
                <Smartphone size={14} /> {t("pages.wearablesX.pairIphone")}
              </button>
              <a href="https://support.apple.com/en-gb/HT204665" target="_blank" rel="noreferrer" className="fl-btn-ghost text-sm">
                <Info size={14} /> About Apple Health
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Latest snapshot from iOS bridge */}
      {s && (
        <div className="mb-8" data-testid="health-summary-card">
          <div className="fl-label mb-3 flex items-center gap-2"><HeartPulse size={12} className="text-[#f47a20]" /> {t("pages.wearablesX.todayActivity")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <SnapshotStat label="Steps" value={s.steps != null ? Math.round(s.steps).toLocaleString() : null} />
            <SnapshotStat label="Distance" value={s.walkingRunningDistanceKm != null ? `${s.walkingRunningDistanceKm.toFixed(2)} km` : null} />
            <SnapshotStat label="Active energy" value={s.activeEnergyKcal != null ? `${Math.round(s.activeEnergyKcal)} kcal` : null} />
            <SnapshotStat label="Exercise" value={s.exerciseMinutes != null ? `${Math.round(s.exerciseMinutes)} min` : null} />
            <SnapshotStat label="Resting HR" value={s.restingHeartRateBPM != null ? `${Math.round(s.restingHeartRateBPM)} bpm` : null} />
            <SnapshotStat label="HRV" value={s.heartRateVariabilityMs != null ? `${s.heartRateVariabilityMs.toFixed(0)} ms` : null} />
            <SnapshotStat label="Sleep" value={s.sleepMinutes != null ? `${(s.sleepMinutes / 60).toFixed(1)} h` : null} />
            <SnapshotStat label="VO₂ max" value={s.vo2MaxMlPerKgPerMin != null ? `${s.vo2MaxMlPerKgPerMin.toFixed(1)}` : null} />
            <SnapshotStat label="SpO₂" value={s.oxygenSaturationPercent != null ? `${s.oxygenSaturationPercent.toFixed(1)}%` : null} />
            <SnapshotStat label="Weight" value={s.bodyWeightKg != null ? `${s.bodyWeightKg.toFixed(1)} kg` : null} />
            <SnapshotStat label="Mindful" value={s.mindfulMinutes != null ? `${Math.round(s.mindfulMinutes)} min` : null} />
            <SnapshotStat label="Workouts" value={s.workouts?.length ? `${s.workouts.length}` : null} />
          </div>
          {snapshot?.recorded_at && (
            <div className="text-[11px] mt-3" style={{ color: "var(--fl-muted-2)" }}>
              Last synchronised {new Date(snapshot.recorded_at).toLocaleString("en-GB")} · from {snapshot.device_name || "iPhone"}
            </div>
          )}
        </div>
      )}

      {/* Consent categories */}
      <div className="fl-card p-6 mb-8" data-testid="consent-card">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="fl-label flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#f47a20]" /> {t("pages.wearablesX.consentEyebrow")}</div>
            <div className="fl-heading text-xl mt-1">{consent.granted ? t("pages.wearablesX.sharingOn") : t("pages.wearablesX.chooseAndShare")}</div>
            <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>
              {t("pages.wearablesX.consentSub")}
            </div>
          </div>
          {consent.granted ? (
            <button className="fl-btn-ghost text-sm" onClick={() => saveConsent(false)} data-testid="revoke-consent"><X size={14} /> {t("pages.wearablesX.turnOff")}</button>
          ) : (
            <button className="fl-btn text-sm" onClick={() => saveConsent(true)} data-testid="grant-consent"><ShieldCheck size={14} /> {t("pages.wearablesX.agreeConnect")}</button>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
          {HEALTH_CATEGORIES.map((c) => {
            const Icon = c.icon;
            const on = consent.categories.includes(c.key);
            return (
              <label
                key={c.key}
                className={`fl-card p-3 flex items-start gap-3 cursor-pointer transition-colors ${on ? "" : "opacity-70"}`}
                style={on ? { borderColor: "var(--fl-orange)" } : {}}
                data-testid={`consent-cat-${c.key}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggleCategory(c.key)} className="mt-1 accent-[#f47a20]" />
                <Icon size={16} className={on ? "text-[#f47a20]" : ""} style={on ? {} : { color: "var(--fl-muted-2)" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: "var(--fl-text)" }}>{c.label}</div>
                  <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>{c.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
        {consent.categories.length > 0 && (
          <button className="fl-btn-ghost text-xs mt-3" onClick={() => saveConsent(consent.granted)} data-testid="save-categories">Save selection</button>
        )}
      </div>

      {/* Paired devices */}
      {devices.length > 0 && (
        <div className="fl-card p-6 mb-8" data-testid="paired-devices-card">
          <div className="fl-label mb-3 flex items-center gap-2"><Smartphone size={12} className="text-[#f47a20]" /> Paired iPhones</div>
          <div className="space-y-2">
            {devices.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-none" style={{ borderColor: "var(--fl-border)" }} data-testid={`device-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "var(--fl-text)" }}>
                    {d.device_name || "Unnamed iPhone"} {d.status === "pending" && <span className="text-[10px] text-[#EAB308] ml-2">waiting for first sync</span>}
                    {d.status === "active" && <span className="text-[10px] text-[#22C55E] ml-2">active</span>}
                  </div>
                  <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                    {d.device_model || "iOS device"} · {d.last_seen ? `last sync ${new Date(d.last_seen).toLocaleString("en-GB")}` : `created ${new Date(d.created_at).toLocaleString("en-GB")}`}
                  </div>
                </div>
                <button className="fl-btn-ghost text-xs" onClick={() => revokeDevice(d.id)} data-testid={`device-revoke-${d.id}`}><Trash2 size={12} /> Revoke</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other providers */}
      <div className="flex items-center justify-between mb-2 mt-2">
        <div className="fl-label">Other devices</div>
        <div className="flex items-center gap-2">
          {editLayout && provDirty && (
            <button type="button" onClick={resetProv}
                    className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-white/10"
                    data-testid="wearables-layout-reset">Reset</button>
          )}
          <button type="button" onClick={() => {}}
                  className="hidden"
                  data-testid="wearables-edit-toggle">
            <GripVertical size={12} /> {editLayout ? "Done" : "Edit"}
          </button>
        </div>
      </div>
      <SortableGrid
        items={provOrder}
        onReorder={setProvOrder}
        onHide={hideProv}
        onResize={sizeProv}
        sizes={provSizes}
        editMode={editLayout}
        className="grid md:grid-cols-3 gap-3 mb-8"
      >
        {provOrder.map((pid) => {
          const p = PROVIDERS.find((x) => x.id === pid);
          if (!p) return null;
          const Icon = p.icon;
          const st = status(p.id);
          const connected = st === "connected";
          return (
            <SortableGrid.Item key={p.id} id={p.id}>
            <div className={`fl-card p-5 h-full ${connected ? "border-[#f47a20]/50" : ""} ${p.locked ? "opacity-70" : ""}`} data-testid={`wear-${p.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <Icon className={connected ? "text-[#f47a20]" : ""} size={22} style={connected ? {} : { color: "var(--fl-muted)" }} />
                <div className="flex-1">
                  <div className="fl-heading text-base flex items-center gap-1.5">{p.name} {p.locked && <Lock size={12} style={{ color: "var(--fl-muted-2)" }} />}</div>
                  <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{p.desc}</div>
                </div>
              </div>
              {p.locked ? (
                <button disabled className="fl-btn-ghost text-xs w-full justify-center" data-testid={`wear-locked-${p.id}`}>Coming soon</button>
              ) : connected ? (
                <button onClick={() => disconnect(p.id)} className="fl-btn-ghost text-xs w-full" data-testid={`wear-disconnect-${p.id}`}>Disconnect</button>
              ) : (
                <button onClick={() => connect(p.id)} className="fl-btn text-xs w-full justify-center" data-testid={`wear-connect-${p.id}`}>Connect</button>
              )}
            </div>
            </SortableGrid.Item>
          );
        })}
      </SortableGrid>

      {/* Manual entry (kept for prescribers / when no device present) */}
      <div className="fl-card p-6 mb-6">
        <div className="fl-heading text-xl mb-4">Add today manually</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <div className="fl-label mb-1">Date</div>
            <input type="date" className="fl-input" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} data-testid="wear-date" />
          </div>
          {[["steps", "Steps"], ["resting_hr", "Resting HR"], ["sleep_minutes", "Sleep (min)"], ["active_minutes", "Active (min)"]].map(([k, l]) => (
            <div key={k}>
              <div className="fl-label mb-1">{l}</div>
              <input type="number" className="fl-input" value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} data-testid={`wear-${k}`} />
            </div>
          ))}
        </div>
        <button className="fl-btn mt-4" onClick={addManual} data-testid="wear-add"><Plus size={14} /> Save</button>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="fl-card p-6">
          <div className="fl-heading text-xl mb-4">Steps · last 30 days</div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                <YAxis stroke="#71717A" fontSize={10} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid #333" }} />
                <Line dataKey="steps" stroke="#f47a20" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm" style={{ color: "var(--fl-muted-2)" }}>No data yet.</div>}
        </div>
        <div className="fl-card p-6">
          <div className="fl-heading text-xl mb-4">Resting HR & Sleep (hours)</div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                <YAxis stroke="#71717A" fontSize={10} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid #333" }} />
                <Line dataKey="hr" stroke="#EF4444" strokeWidth={2} dot={false} />
                <Line dataKey="sleep" stroke="#22C55E" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm" style={{ color: "var(--fl-muted-2)" }}>No data yet.</div>}
        </div>
      </div>

      {showPair && pairing && <PairModal pairing={pairing} onClose={() => setShowPair(false)} appleConn={appleConn} />}
    </DashLayout>
  );
}

function SnapshotStat({ label, value }) {
  if (value == null) return null;
  return (
    <div className="fl-card p-3" data-testid={`snap-${label.replace(/\W/g, "-").toLowerCase()}`}>
      <div className="fl-label">{label}</div>
      <div className="fl-heading text-xl mt-1">{value}</div>
    </div>
  );
}

function PairModal({ pairing, onClose, appleConn }) {
  const [tab, setTab] = useState("code"); // code | swift
  const ingestFull = `${window.location.origin}/api${pairing.ingest_url.startsWith("/api") ? pairing.ingest_url.slice(4) : pairing.ingest_url}`;
  const activateFull = `${window.location.origin}/api${pairing.activate_url.startsWith("/api") ? pairing.activate_url.slice(4) : pairing.activate_url}`;
  const qrData = JSON.stringify({ token: pairing.token, ingestUrl: ingestFull, activateUrl: activateFull });
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrData)}`;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };
  const swift = `// Save to your iOS project as HealthDataUploadService.swift
let baseURL = URL(string: "${window.location.origin}")!
let deviceToken = "${pairing.token}"

// 1) Activate the pairing on first launch
var activate = URLRequest(url: baseURL.appending(path: "/api/wearables/pair/activate"))
activate.httpMethod = "POST"
activate.setValue("Bearer \\(deviceToken)", forHTTPHeaderField: "Authorization")
activate.setValue("application/json", forHTTPHeaderField: "Content-Type")
activate.httpBody = try JSONSerialization.data(withJSONObject: [
    "device_name": UIDevice.current.name,
    "device_model": UIDevice.current.model,
    "os_version": UIDevice.current.systemVersion,
    "app_version": "1.0.0",
])

// 2) Later, upload a DailyHealthSnapshot
var upload = URLRequest(url: baseURL.appending(path: "/api/v1/client-health/daily-snapshot"))
upload.httpMethod = "POST"
upload.setValue("Bearer \\(deviceToken)", forHTTPHeaderField: "Authorization")
upload.setValue("application/json", forHTTPHeaderField: "Content-Type")
let encoder = JSONEncoder(); encoder.dateEncodingStrategy = .iso8601
upload.httpBody = try encoder.encode(snapshot)   // snapshot: DailyHealthSnapshot
_ = try await URLSession.shared.data(for: upload)`;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start md:items-center justify-center" data-testid="pair-modal">
      <div className="fl-card w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div>
            <div className="fl-label text-[#f47a20]">Pair Apple Health</div>
            <div className="fl-heading text-xl mt-1">Connect your iPhone in ~30 seconds</div>
          </div>
          <button className="fl-btn-ghost text-xs" onClick={onClose} data-testid="pair-close"><X size={14} /></button>
        </div>

        <div className="flex gap-2 px-4 pt-3 border-b" style={{ borderColor: "var(--fl-border)" }}>
          {[["code", "For clients"], ["swift", "For iOS developers"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs uppercase tracking-wider font-semibold ${tab === k ? "text-[#f47a20] border-b-2 border-[#f47a20] -mb-px" : ""}`}
              style={tab === k ? {} : { color: "var(--fl-muted)" }}
              data-testid={`pair-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "code" ? (
          <div className="p-6 grid md:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="fl-label mb-3">Scan from Functional LAB iOS app</div>
              <img src={qrUrl} alt="Pairing QR" className="mx-auto rounded-md bg-white p-2" data-testid="pair-qr" />
              <div className="mt-4 fl-label">Or enter this code</div>
              <div className="fl-heading text-3xl tracking-widest text-[#f47a20] mt-2" data-testid="pair-code">{pairing.code}</div>
              <button className="fl-btn-ghost text-xs mt-3" onClick={() => copy(pairing.token)} data-testid="pair-copy-token"><Copy size={12} /> Copy raw token</button>
            </div>
            <div className="text-sm space-y-3" style={{ color: "var(--fl-text-2)" }}>
              <div className="fl-label" style={{ color: "var(--fl-orange)" }}>Steps</div>
              <ol className="list-decimal list-inside space-y-2">
                <li>Open the <b>Functional LAB</b> iOS app on your iPhone.</li>
                <li>Tap <b>Connect Apple Health</b>.</li>
                <li>Scan this QR or type the code shown above.</li>
                <li>Approve Apple's HealthKit permission for the categories you want to share.</li>
                <li>Daily summaries will sync automatically to your dashboard.</li>
              </ol>
              <div className="text-[11px] pt-3 border-t" style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted-2)" }}>
                Token expires in {Math.round((pairing.expires_in_seconds || 900) / 60)} min if never activated. You can revoke a paired device any time.
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="text-xs mb-2" style={{ color: "var(--fl-muted)" }}>
              Endpoint contract for the iOS bridge. Uses the device token as a Bearer credential — no password required.
            </div>
            <div className="fl-card p-3 text-[11px] font-mono overflow-x-auto" style={{ background: "var(--fl-bg-3)" }}>
              <div><span style={{ color: "var(--fl-orange)" }}>POST</span> {activateFull}</div>
              <div><span style={{ color: "var(--fl-orange)" }}>POST</span> {ingestFull}</div>
              <div><span style={{ color: "var(--fl-orange)" }}>Authorization:</span> Bearer &lt;deviceToken&gt;</div>
            </div>
            <div className="fl-label mt-4 mb-1">Swift snippet</div>
            <pre className="fl-card p-3 text-[11px] overflow-x-auto whitespace-pre" style={{ background: "var(--fl-bg-3)", color: "var(--fl-text-2)" }}>{swift}</pre>
            <button className="fl-btn-ghost text-xs mt-3" onClick={() => copy(swift)}><Copy size={12} /> Copy snippet</button>
            <div className="text-[11px] mt-4" style={{ color: "var(--fl-muted-2)" }}>
              See <a href="https://developer.apple.com/documentation/healthkit/authorizing-access-to-health-data" target="_blank" rel="noreferrer" className="text-[#f47a20] underline">Apple's HealthKit authorization guide</a> for the read-permission implementation.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
